import { useReducer, useCallback } from "react";
import { flushSync } from "react-dom";
import GridReportService from "../../../_services/GridReportService";
import {
  ReportTypeItem,
  AxisChoice,
  GridData,
  FilterDefinition,
  ActiveFilter,
  CannedReport,
  CannedReportArgs,
} from "../types";

/** Force React to commit state to DOM, then wait for the browser to paint */
const waitForPaint = (): Promise<void> =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

/**
 * Normalize a stored date to ISO `YYYY-MM-DD` so it both displays in the native
 * date input and is accepted by MySQL. Saved/canned reports may hold
 * `DD/MM/YYYY` (or `DD-MM-YYYY`); anything unrecognized becomes "".
 */
const toISODate = (raw?: string): string => {
  const s = (raw || "").trim();
  if (!s) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const d = (m[1] as string).padStart(2, "0");
    const mo = (m[2] as string).padStart(2, "0");
    const y = m[3] as string;
    return `${y}-${mo}-${d}`;
  }
  return "";
};

// ==================== State ====================

export interface GridReportState {
  // Config data
  reportTypes: ReportTypeItem[];
  axisChoices: AxisChoice[];
  filterDefs: FilterDefinition[];

  // User selections
  selectedReportType: string;
  selectedXDimension: string;
  selectedYDimension: string;
  startDate: string;
  endDate: string;
  activeFilters: ActiveFilter[];

  // Grid data
  gridData: GridData | null;

  // UI state
  loading: boolean;
  configLoading: boolean;
  error: string | null;
}

const initialState: GridReportState = {
  reportTypes: [],
  axisChoices: [],
  filterDefs: [],
  selectedReportType: "",
  selectedXDimension: "",
  selectedYDimension: "",
  startDate: "",
  endDate: "",
  activeFilters: [],
  gridData: null,
  loading: false,
  configLoading: false,
  error: null,
};

// ==================== Actions ====================

type Action =
  | { type: "SET_REPORT_TYPES"; payload: ReportTypeItem[] }
  | { type: "SET_AXIS_CHOICES"; payload: AxisChoice[] }
  | { type: "SET_FILTER_DEFS"; payload: FilterDefinition[] }
  | { type: "SET_REPORT_TYPE"; payload: string }
  | { type: "SET_X_DIMENSION"; payload: string }
  | { type: "SET_Y_DIMENSION"; payload: string }
  | { type: "SET_START_DATE"; payload: string }
  | { type: "SET_END_DATE"; payload: string }
  | { type: "SET_FILTER"; payload: ActiveFilter }
  | { type: "CLEAR_FILTERS" }
  | { type: "SET_GRID_DATA"; payload: GridData }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_CONFIG_LOADING"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "RESET_GRID" }
  | { type: "RESTORE_CANNED"; payload: CannedReportArgs };

function reducer(state: GridReportState, action: Action): GridReportState {
  switch (action.type) {
    case "SET_REPORT_TYPES":
      return { ...state, reportTypes: action.payload };
    case "SET_AXIS_CHOICES":
      return { ...state, axisChoices: action.payload };
    case "SET_FILTER_DEFS":
      return { ...state, filterDefs: action.payload };
    case "SET_REPORT_TYPE":
      return {
        ...state,
        selectedReportType: action.payload,
        selectedXDimension: "",
        selectedYDimension: "",
        axisChoices: [],
        filterDefs: [],
        gridData: null,
        activeFilters: [],
        error: null,
      };
    case "SET_X_DIMENSION":
      return { ...state, selectedXDimension: action.payload };
    case "SET_Y_DIMENSION":
      return { ...state, selectedYDimension: action.payload };
    case "SET_START_DATE":
      return { ...state, startDate: action.payload };
    case "SET_END_DATE":
      return { ...state, endDate: action.payload };
    case "SET_FILTER": {
      const existing = state.activeFilters.filter(
        (f) => f.dimension !== action.payload.dimension
      );
      if (action.payload.values.length > 0) {
        existing.push(action.payload);
      }
      return { ...state, activeFilters: existing };
    }
    case "CLEAR_FILTERS":
      return { ...state, activeFilters: [] };
    case "SET_GRID_DATA":
      return { ...state, gridData: action.payload, error: null };
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_CONFIG_LOADING":
      return { ...state, configLoading: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload, loading: false };
    case "RESET_GRID":
      return { ...state, gridData: null, error: null };
    case "RESTORE_CANNED":
      return {
        ...state,
        selectedReportType: action.payload.type,
        selectedXDimension: action.payload.x,
        selectedYDimension: action.payload.y,
        startDate: toISODate(action.payload.startDate),
        endDate: toISODate(action.payload.endDate),
        activeFilters: action.payload.filters || [],
        gridData: null,
        error: null,
      };
    default:
      return state;
  }
}

// ==================== Hook ====================

export function useGridReport() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const loadReportTypes = useCallback(async () => {
    dispatch({ type: "SET_CONFIG_LOADING", payload: true });
    try {
      const types = await GridReportService.getConfig();
      dispatch({ type: "SET_REPORT_TYPES", payload: types });
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload: err.message || "Failed to load report types",
      });
    } finally {
      dispatch({ type: "SET_CONFIG_LOADING", payload: false });
    }
  }, []);

  const loadAxisChoices = useCallback(async (reportType: string) => {
    try {
      const choices = await GridReportService.getAxisChoices(reportType);
      dispatch({ type: "SET_AXIS_CHOICES", payload: choices });
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload: err.message || "Failed to load axis choices",
      });
    }
  }, []);

  const loadFilters = useCallback(async (reportType: string) => {
    try {
      const filters = await GridReportService.getFilters(reportType);
      dispatch({ type: "SET_FILTER_DEFS", payload: filters });
    } catch (err: any) {
      dispatch({
        type: "SET_ERROR",
        payload: err.message || "Failed to load filters",
      });
    }
  }, []);

  const executeReport = useCallback(
    async (xPage?: number, yPage?: number) => {
      if (
        !state.selectedReportType ||
        !state.selectedXDimension ||
        !state.selectedYDimension
      ) {
        dispatch({
          type: "SET_ERROR",
          payload: "Please select report type, X axis, and Y axis",
        });
        return;
      }

      // flushSync forces React to commit loading=true to DOM synchronously,
      // then waitForPaint ensures the browser actually paints it before the
      // API call starts — prevents fast responses from skipping the loading UI.
      flushSync(() => {
        dispatch({ type: "SET_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: null });
      });
      await waitForPaint();

      try {
        const data = await GridReportService.execute({
          reportType: state.selectedReportType,
          xDimension: state.selectedXDimension,
          yDimension: state.selectedYDimension,
          startDate: state.startDate || undefined,
          endDate: state.endDate || undefined,
          filters:
            state.activeFilters.length > 0 ? state.activeFilters : undefined,
          xPage: xPage ?? state.gridData?.pagination.xPage ?? 0,
          yPage: yPage ?? state.gridData?.pagination.yPage ?? 0,
        });
        dispatch({ type: "SET_GRID_DATA", payload: data });
      } catch (err: any) {
        dispatch({
          type: "SET_ERROR",
          payload: err.response?.data?.error || err.message || "Failed to execute report",
        });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [state.selectedReportType, state.selectedXDimension, state.selectedYDimension, state.startDate, state.endDate, state.activeFilters, state.gridData]
  );

  const setReportType = useCallback((type: string) => {
    dispatch({ type: "SET_REPORT_TYPE", payload: type });
  }, []);

  const setXDimension = useCallback((dim: string) => {
    dispatch({ type: "SET_X_DIMENSION", payload: dim });
  }, []);

  const setYDimension = useCallback((dim: string) => {
    dispatch({ type: "SET_Y_DIMENSION", payload: dim });
  }, []);

  const setStartDate = useCallback((date: string) => {
    dispatch({ type: "SET_START_DATE", payload: date });
  }, []);

  const setEndDate = useCallback((date: string) => {
    dispatch({ type: "SET_END_DATE", payload: date });
  }, []);

  const setFilter = useCallback(
    (dimension: string, values: (number | string)[]) => {
      dispatch({ type: "SET_FILTER", payload: { dimension, values } });
    },
    []
  );

  const clearFilters = useCallback(() => {
    dispatch({ type: "CLEAR_FILTERS" });
  }, []);

  const loadCannedReport = useCallback(
    async (canned: CannedReport) => {
      const { args } = canned;

      // 1. Restore all selections + show loading immediately
      flushSync(() => {
        dispatch({ type: "RESTORE_CANNED", payload: args });
        dispatch({ type: "SET_LOADING", payload: true });
        dispatch({ type: "SET_ERROR", payload: null });
      });
      await waitForPaint();

      // 2. Load axis choices + filters for the report type
      try {
        const [choices, filters] = await Promise.all([
          GridReportService.getAxisChoices(args.type),
          GridReportService.getFilters(args.type),
        ]);
        dispatch({ type: "SET_AXIS_CHOICES", payload: choices });
        dispatch({ type: "SET_FILTER_DEFS", payload: filters });
      } catch (err: any) {
        dispatch({
          type: "SET_ERROR",
          payload: err.message || "Failed to load report config",
        });
        return;
      }

      // 3. Auto-execute
      try {
        const data = await GridReportService.execute({
          reportType: args.type,
          xDimension: args.x,
          yDimension: args.y,
          startDate: args.startDate || undefined,
          endDate: args.endDate || undefined,
          filters: args.filters?.length > 0 ? args.filters : undefined,
          xPage: 0,
          yPage: 0,
        });
        dispatch({ type: "SET_GRID_DATA", payload: data });
      } catch (err: any) {
        dispatch({
          type: "SET_ERROR",
          payload:
            err.response?.data?.error ||
            err.message ||
            "Failed to execute report",
        });
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    []
  );

  return {
    state,
    loadReportTypes,
    loadAxisChoices,
    loadFilters,
    executeReport,
    setReportType,
    setXDimension,
    setYDimension,
    setStartDate,
    setEndDate,
    setFilter,
    clearFilters,
    loadCannedReport,
  };
}
